import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  CipaElectionLiveTrackingDto,
  CipaElectionLiveTrackingResponseDto,
  CipaElectionVoteCreateDto,
  CipaElectionVoteReceiptDto,
  CipaElectionVotingExperienceDto,
} from './voting.models';

interface ActiveElectionListResponse {
  items: { id: number }[];
  total: number;
}

interface VoteResponse {
  electionId: number;
  electionName: string;
  receiptProtocol: string;
  votedAt: string;
  votesRegistered: number;
}

@Injectable({
  providedIn: 'root',
})
export class VotingService {
  private readonly baseUrl = `${environment.smocipaApiBaseUrl}/cipa/voting/elections`;
  private readonly electionsUrl = `${environment.smocipaApiBaseUrl}/cipa/elections`;

  constructor(private readonly http: HttpClient) { }

  listActiveElections(): Observable<ActiveElectionListResponse> {
    return this.http.get<ActiveElectionListResponse>(`${this.baseUrl}/active`);
  }

  getActiveElectionCount(): Observable<number> {
    return this.listActiveElections().pipe(
      map((response) => response.total ?? response.items.length),
    );
  }

  getVotingExperience(companySuffix: string, electionGuid: string): Observable<CipaElectionVotingExperienceDto> {
    return this.http.get<CipaElectionVotingExperienceDto>(
      `${this.baseUrl}/${encodeURIComponent(companySuffix)}/${encodeURIComponent(electionGuid)}/experience`,
    );
  }

  checkForActiveElection(): Observable<boolean> {
    return this.getActiveElectionCount().pipe(
      map((count) => count > 0),
    );
  }

  checkElectionStatus(electionId: number): Observable<{ id: number; status: 'active' | 'inactive' }> {
    return this.listActiveElections().pipe(
      map((response) => ({
        id: electionId,
        status: response.items.some((e) => e.id === electionId) ? 'active' : 'inactive',
      })),
    );
  }
  
  getTracking(electionId: number): Observable<CipaElectionLiveTrackingResponseDto> {
    return this.http.get<CipaElectionLiveTrackingResponseDto>(`${this.electionsUrl}/${electionId}/tracking`);
  }

  submitVote(input: CipaElectionVoteCreateDto): Observable<CipaElectionVoteReceiptDto> {
    return this.http
      .post<VoteResponse>(`${this.baseUrl}/${input.electionId}/vote`, { candidateIds: input.candidateIds })
      .pipe(
        map((r) => ({
          protocol: r.receiptProtocol,
          registeredAt: r.votedAt,
          electionId: r.electionId,
          electionName: r.electionName,
          voterName: '',
          selectedCandidateIds: input.candidateIds,
        })),
      );
  }
}
